import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { Textarea } from './textarea';
function InputGroup({ className, ...props }) {
    return (_jsx("div", { className: cn('group/input-group border-input relative flex w-full items-center rounded-md border shadow-xs transition-[color,box-shadow] outline-none', 'h-9 has-[>textarea]:h-auto', 
        // Variants based on alignment.
        'has-[>[data-align=inline-start]]:[&>input]:pl-2', 'has-[>[data-align=inline-end]]:[&>input]:pr-2', 'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3', 'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3', 
        // Focus state.
        'has-[[data-slot=input-group-control]:focus-visible]:ring-ring has-[[data-slot=input-group-control]:focus-visible]:ring-1', 
        // Error state.
        'has-[[data-slot][aria-invalid=true]]:ring-destructive/20 has-[[data-slot][aria-invalid=true]]:border-destructive', className), "data-slot": "input-group", role: "group", ...props }));
}
const inputGroupAddonVariants = cva("text-muted-foreground flex h-auto cursor-text select-none items-center justify-center gap-2 py-1.5 text-sm font-medium group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4", {
    defaultVariants: {
        align: 'inline-start',
    },
    variants: {
        align: {
            'block-end': '[.border-t]:pt-3 order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5',
            'block-start': '[.border-b]:pb-3 order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5',
            'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.4rem] has-[>kbd]:mr-[-0.35rem]',
            'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
        },
    },
});
function InputGroupAddon({ className, align = 'inline-start', ...props }) {
    return (_jsx("div", { className: cn(inputGroupAddonVariants({ align }), className), "data-align": align, "data-slot": "input-group-addon", onClick: (e) => {
            if (e.target.closest('button')) {
                return;
            }
            e.currentTarget.parentElement?.querySelector('input')?.focus();
        }, role: "group", ...props }));
}
const inputGroupButtonVariants = cva('flex items-center gap-2 text-sm shadow-none', {
    defaultVariants: {
        size: 'xs',
    },
    variants: {
        size: {
            'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
            'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
            'sm': 'h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5',
            'xs': "h-6 gap-1 rounded-[calc(var(--radius)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        },
    },
});
function InputGroupButton({ className, size = 'xs', type = 'button', variant = 'ghost', ...props }) {
    return (_jsx(Button, { className: cn(inputGroupButtonVariants({ size }), className), "data-size": size, type: type, variant: variant, ...props }));
}
function InputGroupInput({ className, ...props }) {
    return (_jsx(Input, { className: cn('flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0', className), "data-slot": "input-group-control", ...props }));
}
function InputGroupText({ className, ...props }) {
    return (_jsx("span", { className: cn("text-muted-foreground flex items-center gap-2 text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4", className), ...props }));
}
function InputGroupTextarea({ className, ...props }) {
    return (_jsx(Textarea, { className: cn('flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none focus-visible:ring-0', className), "data-slot": "input-group-control", ...props }));
}
export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText, InputGroupTextarea, };
